import { useCallback, useEffect, useState } from "react";
import { Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { MemberPageScaffold } from "@/components/MemberScaffold";
import {
  AppButton,
  AppEmptyState,
  AppLoadingState,
  AppCard,
  AppCardBody,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

type NotificationActor = Pick<Tables<"profiles">, "display_name" | "avatar_url">;
type NotificationRow = Tables<"notifications"> & {
  actor: NotificationActor | null;
};

const Notifications = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;

    const { data } = await supabase
      .from("notifications")
      .select("*, actor:profiles!notifications_actor_id_fkey(display_name, avatar_url)")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(50);

    setNotifications((data ?? []) as NotificationRow[]);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel("my-notifications")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        () => void fetchNotifications(),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchNotifications, user]);

  const unreadCount = notifications.filter((notification) => !notification.is_read).length;
  const notificationSubtitle =
    unreadCount > 0
      ? `You have ${unreadCount} update${unreadCount === 1 ? "" : "s"} waiting. Start with what matters most right now.`
      : "A calmer place to keep track of replies, requests, messages, and anything that needs your attention.";

  const markAllRead = async () => {
    if (!user) return;

    await supabase
      .from("notifications")
      .update({ is_read: true })
      .eq("user_id", user.id)
      .eq("is_read", false);

    setNotifications((current) =>
      current.map((notification) => ({ ...notification, is_read: true })),
    );
    await fetchNotifications();
  };

  const handleTap = async (notification: NotificationRow) => {
    if (!notification.is_read) {
      await supabase.from("notifications").update({ is_read: true }).eq("id", notification.id);
      setNotifications((current) =>
        current.map((item) => (item.id === notification.id ? { ...item, is_read: true } : item)),
      );
    }

    switch (notification.entity_type) {
      case "adventure":
        navigate(`/adventures/${notification.entity_id}`);
        break;
      case "post":
        navigate(`/community/feed/${notification.entity_id}`);
        break;
      case "channel":
        navigate(`/community/channel/${notification.entity_id}`);
        break;
      case "room":
        navigate(`/community/room/${notification.entity_id}`);
        break;
      case "event":
        navigate(`/whats-on/${notification.entity_id}`);
        break;
      case "connection":
        navigate(
          notification.actor_id
            ? `/community/member/${notification.actor_id}`
            : notification.entity_id
              ? `/community/member/${notification.entity_id}`
              : "/community/members",
        );
        break;
      case "message":
        navigate(`/messages/${notification.entity_id}`);
        break;
      case "admin_event_review_queue":
        navigate("/admin/events");
        break;
      case "admin_moderation_queue":
        navigate("/admin/moderation");
        break;
      case "admin_content_review_queue":
        navigate("/admin/content");
        break;
      case "admin_account_deletion_queue":
        navigate("/admin/account-deletion");
        break;
      default:
        break;
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffHours = (now.getTime() - date.getTime()) / 3600000;

    if (diffHours < 1) return `${Math.max(1, Math.floor(diffHours * 60))}m ago`;
    if (diffHours < 24) return `${Math.floor(diffHours)}h ago`;
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  const formatMeta = (notification: NotificationRow) =>
    notification.is_read ? formatTime(notification.created_at) : `Unread · ${formatTime(notification.created_at)}`;

  return (
    <MemberPageScaffold
      title="Notifications"
      subtitle={notificationSubtitle}
      fallbackPath="/my-kindred"
      action={
        unreadCount > 0 ? (
          <AppButton tone="outline" size="sm" onPress={markAllRead}>
            <Check size={14} />
            Mark all read
          </AppButton>
        ) : undefined
      }
    >
      {loading ? (
        <AppLoadingState
          title="Loading notifications"
          body="Gathering the latest updates from across Kindred."
        />
      ) : notifications.length === 0 ? (
        <AppEmptyState
          title="No notifications yet"
          body="When someone reaches out or something needs your attention, it will show up here."
        />
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <AppCard key={notification.id} backgroundColor={notification.is_read ? "$cardBackground" : "$primarySoft"}>
              <AppCardBody>
                <button
                  type="button"
                  onClick={() => void handleTap(notification)}
                  className="flex w-full items-start gap-3 text-left"
                >
                  <Avatar className="h-10 w-10 shrink-0">
                    {notification.actor?.avatar_url ? (
                      <AvatarImage src={notification.actor.avatar_url} alt={notification.actor.display_name ?? "Member"} />
                    ) : null}
                    <AvatarFallback className="bg-primary/20 text-primary text-sm font-semibold">
                      {(notification.actor?.display_name ?? "K")[0]}
                    </AvatarFallback>
                  </Avatar>

                  <div className="min-w-0 flex-1">
                    <AppText
                      fontFamily={notification.is_read ? kindredFontFamilies.sans : kindredFontFamilies.sansBold}
                      fontSize="$sm"
                      lineHeight="$sm"
                    >
                      {notification.body}
                    </AppText>
                    <AppText color="$colorMuted" fontSize="$xs" lineHeight="$xs">
                      {formatMeta(notification)}
                    </AppText>
                  </div>

                  {!notification.is_read ? (
                    <span className="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-accent" />
                  ) : null}
                </button>
              </AppCardBody>
            </AppCard>
          ))}
        </div>
      )}
    </MemberPageScaffold>
  );
};

export default Notifications;
